import { Check, AlertTriangle, AlertCircle, HelpCircle } from "lucide-react";
import { STATUS_STYLES } from "@/lib/format";

const ICONS = {
  "COMPLIANT": Check,
  "NOT APPLICABLE": Check,
  "NOT DISCLOSED": AlertTriangle,
  "DISCLOSED": AlertCircle,
  "INCOMPLETE": AlertTriangle,
  "MISSING ALLTOGETHER": HelpCircle,
  "NOT STATED": HelpCircle,
};

const DisclosureRow = ({ item, twoState = false }) => {
  const rawStatus = item?.status || "COMPLIANT";

  // Warranty section: collapse to exactly two outcomes —
  //  • GREEN  = the warranty status is disclosed (Yes it has a warranty, OR No it doesn't)
  //  • YELLOW = a dedicated Yes/No checkbox exists but neither box is ticked (blank field)
  let status = rawStatus;
  let style, Icon, label;
  if (twoState) {
    const unselected = rawStatus === "NOT DISCLOSED"; // printed Yes/No spot present, neither marked
    const yes = rawStatus === "DISCLOSED";            // answered Yes (this item applies)
    const isMfr = item.key === "manufacturer_warranty_cancelled";
    if (unselected) {
      // A dedicated Yes/No spot exists but neither is selected → yellow, NOT compliant (-10 pts).
      status = "NOT DISCLOSED";
      style = STATUS_STYLES["NOT DISCLOSED"];
      Icon = AlertTriangle;
      label = "NOT SELECTED";
    } else if (isMfr && yes) {
      // Manufacturer's warranty marked cancelled → still compliant, but yellow & needs review.
      status = "DISCLOSED";
      style = STATUS_STYLES["DISCLOSED"];
      Icon = AlertCircle;
      label = "CANCELLED - REVIEW";
    } else {
      // Compliant / green outcome.
      status = "COMPLIANT";
      style = STATUS_STYLES["COMPLIANT"];
      Icon = Check;
      if (isMfr) {
        label = "COMPLIANT";
      } else if (item.key === "extended_warranty") {
        label = yes ? "HAS EXT. WARRANTY" : "NONE";
      } else {
        // dealer_guaranty (dealer-provided warranty/guaranty)
        label = yes ? "HAS WARRANTY" : "NONE";
      }
    }
  } else {
    style = STATUS_STYLES[rawStatus] || STATUS_STYLES["COMPLIANT"];
    Icon = ICONS[rawStatus] || Check;
    label = style.label;
  }

  return (
    <div data-testid={`disclosure-${item.key}`} data-status={status} className="border-b border-border/60 last:border-b-0">
      <div className="flex items-center gap-3 py-2.5">
        <span className={`h-6 w-1 shrink-0 rounded-full ${style.dot}`} aria-hidden="true" />
        <Icon className={`h-4 w-4 shrink-0 ${style.text}`} aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate text-sm font-600 text-foreground">{item.title}</span>
        <span
          data-testid={`disclosure-${item.key}-badge`}
          className={`shrink-0 rounded-full border ${style.border} bg-card px-2.5 py-0.5 font-mono-plex text-[10px] font-600 uppercase tracking-[0.1em] ${style.text}`}
        >
          {label}
        </span>
      </div>
    </div>
  );
};

const WARRANTY_KEYS = ["manufacturer_warranty_cancelled", "dealer_guaranty", "extended_warranty"];

export const DisclosureList = ({ disclosures = [] }) => {
  if (!disclosures.length) return null;
  const warranty = WARRANTY_KEYS
    .map((k) => disclosures.find((d) => d.key === k))
    .filter(Boolean);
  const main = disclosures.filter((d) => !WARRANTY_KEYS.includes(d.key));
  const flagged = main.filter((d) => d.status === "NOT DISCLOSED" || d.status === "DISCLOSED").length;
  // A warranty is in play when the dealer guaranty or an extended warranty is marked Yes (disclosed).
  const warrantyPresent = warranty.some(
    (d) => (d.key === "dealer_guaranty" || d.key === "extended_warranty") && d.status === "DISCLOSED"
  );

  return (
    <div className="space-y-5">
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-heading text-lg font-800 tracking-tight text-foreground">
            Required Used-Car Disclosures
          </h3>
          <span className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            {flagged > 0 ? `${flagged} to review` : `${main.length} checked`}
          </span>
        </div>
        <div data-testid="disclosure-list" className="grid grid-cols-1 gap-x-6 overflow-hidden rounded-xl border border-border bg-card px-3 py-1 sm:grid-cols-2">
          {main.map((d) => (
            <DisclosureRow key={d.key} item={d} />
          ))}
        </div>
      </div>

      {warranty.length > 0 && (
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-heading text-lg font-800 tracking-tight text-foreground">Vehicle Warranty</h3>
          </div>
          <div data-testid="warranty-disclosure-list" className="grid grid-cols-1 gap-x-6 overflow-hidden rounded-xl border border-border bg-card px-3 py-1 sm:grid-cols-2">
            {warranty.map((d) => (
              <DisclosureRow key={d.key} item={d} twoState />
            ))}
          </div>
          {warrantyPresent && (
            <div
              data-testid="warranty-guidance"
              className="mt-3 rounded-xl border border-attention/40 bg-attention-soft p-5"
            >
              <p className="text-sm leading-relaxed text-muted-foreground">
                Where a vehicle is sold with a warranty, a dealer should fully disclose the details of the
                warranty and provide a copy of the details to the purchaser. The details disclosed in the
                warranty contract should include the following:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground">
                <li>the identity of the warrantor</li>
                <li>the parts and labour that are covered</li>
                <li>any time or distance limits</li>
                <li>
                  obligations of the purchaser and the seller, or warrantor, including conditions and
                  restrictions
                </li>
              </ul>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                The details disclosed in the retail contract regarding the extended warranty should disclose
                an accurate description of the warranty and its retail value.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
